import { useCallback } from 'react';

import {
  type MutateOptions,
  useMutation,
  type UseMutationOptions,
} from '@tanstack/react-query';

import { swallowMutation } from './_utils.js';

/**
 * `useMutation` whose `mutateAsync` never rejects.
 *
 * The house mutation pattern: hooks expose this `mutateAsync`, consumers fire it
 * with per-call `{ onSuccess, onError }` (symmetric — success side effects in
 * `onSuccess`, error side effects in `onError`, generic toasts in the hook-level
 * config). Because the returned promise resolves to `data | undefined` instead
 * of rejecting, consumers can `await` it (to sequence / coordinate) or ignore it
 * entirely without a `try/catch`. See `src/hooks/AGENTS.md`.
 */
export const useSwallowedMutation = <
  TData = unknown,
  TError = unknown,
  TVariables = void,
  TContext = unknown
>(
  options: UseMutationOptions<TData, TError, TVariables, TContext>
) => {
  const { mutateAsync, isPending } = useMutation(options);

  const swallowed = useCallback(
    (variables: TVariables, mutateOptions?: MutateOptions<TData, TError, TVariables, TContext>) =>
      swallowMutation(mutateAsync(variables, mutateOptions)),
    [mutateAsync]
  );

  return { mutateAsync: swallowed, isPending };
};
